import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { useCallback } from 'react';
import { getCanonicalCategoryColor } from '@/features/colors/lib/paletteLookup';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import type { CompleteCategory } from '../types';
import { useCategoryCache } from './useCategoryCache';
import type {
  CreateExperienceCategoryMutation,
  CreateExperienceCategoryMutationVariables,
} from './useCreateCategory.generated';

const query = gql`
  mutation CreateExperienceCategory($input: CreateExperienceCategoryInput!) {
    createExperienceCategory(input: $input) {
      id
      text
      color
      colorName
      sortOrder
    }
  }
`;

export const useCreateCategory = () => {
  const experience = useEffectiveExperience();
  const cache = useCategoryCache();

  const { mutate, isPending } = useMutation<
    CreateExperienceCategoryMutation,
    QueryError,
    CreateExperienceCategoryMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        CreateExperienceCategoryMutation,
        CreateExperienceCategoryMutationVariables
      >(query, variables),
    onMutate: ({ input }) => {
      cache.addCategory(input.categoryId ?? '', {
        color: input.color,
        colorName: input.colorName,
        text: input.text,
        sortOrder: 0,
      });
    },
    onError: (_, { input }) => {
      cache.removeCategory(input.categoryId ?? '');
    },
  });

  const createCategory = useCallback(
    (
      category: CompleteCategory,
      options?: MutateOptions<
        CreateExperienceCategoryMutation,
        QueryError,
        CreateExperienceCategoryMutationVariables
      >
    ) =>
      mutate(
        {
          input: {
            experienceId: experience.id,
            categoryId: category.id,
            // The palette slot is the category's color identity; the required
            // raw `color` is derived from it so the stored fallback hex stays
            // coherent with the slot.
            color: getCanonicalCategoryColor(category.colorName),
            colorName: category.colorName,
            text: category.text || '',
          },
        },
        options
      ),
    [mutate, experience.id]
  );

  return { createCategory, isPending };
};
